import { useState } from 'react';
import {
  Loader2,
  Sparkles,
  UserSearch,
  TrendingUp,
  ShieldCheck,
  Mail,
  AlertTriangle,
  Users,
} from 'lucide-react';
import Button from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import Badge from './ui/Badge';
import api from '../lib/api';

export interface JobDiscoveryReport {
  summary: string;
  talentGaps: string[];
  poolAnalysis: {
    totalKolsInAgency: number;
    matchingExisting: number;
    gapCount: number;
    summary: string;
  };
  discoveryCandidates: Array<{
    name: string;
    platform: string;
    handle: string;
    tier: string;
    estimatedFollowers: number;
    matchScore: number;
    reasons: string[];
    vettingPriority: 'high' | 'medium' | 'low';
  }>;
  recruitmentPriorities: Array<{
    priority: number;
    action: string;
    targetNiche: string;
    rationale: string;
  }>;
  recommendations: string[];
  demo?: boolean;
  cached?: boolean;
}

export interface AgencyDiscoveryReport {
  summary: string;
  portfolioHealth: { score: number; summary: string };
  trendingNiches: Array<{
    niche: string;
    momentum: 'rising' | 'stable' | 'declining';
    suggestedAction: string;
  }>;
  underrepresentedCategories: string[];
  recruitmentPipeline: Array<{ stage: string; count: number; bottleneck: string }>;
  savedSearchOpportunities: string[];
  recommendations: string[];
  demo?: boolean;
  cached?: boolean;
}

export interface KOLVetReport {
  kolName: string;
  overallScore: number;
  authenticityScore: number;
  brandSafetyScore: number;
  contentQualityScore: number;
  flags: Array<{ type: string; severity: 'high' | 'medium' | 'low'; detail: string }>;
  strengths: string[];
  recommendations: string[];
  demo?: boolean;
  cached?: boolean;
}

export interface RecruitmentOutreachDraft {
  subject: string;
  message: string;
  tone: string;
  personalizationTips: string[];
  demo?: boolean;
  cached?: boolean;
}

interface KOLOption {
  id: string;
  name: string;
}

interface AIKOLDiscoveryPanelProps {
  mode: 'job' | 'agency';
  jobId?: string;
  kols?: KOLOption[];
  title?: string;
  className?: string;
}

function priorityVariant(p: string): 'destructive' | 'warning' | 'secondary' {
  if (p === 'high') return 'destructive';
  if (p === 'medium') return 'warning';
  return 'secondary';
}

function severityVariant(s: string): 'destructive' | 'warning' | 'secondary' {
  if (s === 'high') return 'destructive';
  if (s === 'medium') return 'warning';
  return 'secondary';
}

function momentumVariant(m: string): 'success' | 'secondary' | 'warning' {
  if (m === 'rising') return 'success';
  if (m === 'declining') return 'warning';
  return 'secondary';
}

export function AIKOLDiscoveryPanel({
  mode,
  jobId,
  kols = [],
  title = mode === 'job' ? 'KOL Discovery & Recruitment' : 'Agency Talent Scout',
  className = '',
}: AIKOLDiscoveryPanelProps) {
  const [scanLoading, setScanLoading] = useState(false);
  const [vetLoading, setVetLoading] = useState(false);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobReport, setJobReport] = useState<JobDiscoveryReport | null>(null);
  const [agencyReport, setAgencyReport] = useState<AgencyDiscoveryReport | null>(null);
  const [vetReport, setVetReport] = useState<KOLVetReport | null>(null);
  const [outreach, setOutreach] = useState<RecruitmentOutreachDraft | null>(null);
  const [selectedKolId, setSelectedKolId] = useState(kols[0]?.id ?? '');

  const runScan = async () => {
    setScanLoading(true);
    setError(null);
    try {
      const endpoint = mode === 'job' ? '/ai/discovery/job-scan' : '/ai/discovery/agency-scan';
      const payload = mode === 'job' ? { jobId } : {};
      const res = await api.post(endpoint, payload);
      if (mode === 'job') setJobReport(res.data);
      else setAgencyReport(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to run discovery scan';
      setError(msg);
    } finally {
      setScanLoading(false);
    }
  };

  const runVet = async () => {
    if (!selectedKolId) {
      setError('Select a KOL to vet');
      return;
    }
    setVetLoading(true);
    setError(null);
    try {
      const res = await api.post('/ai/discovery/vet', {
        kolProfileId: selectedKolId,
        jobId: mode === 'job' ? jobId : undefined,
      });
      setVetReport(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to vet KOL';
      setError(msg);
    } finally {
      setVetLoading(false);
    }
  };

  const runOutreach = async () => {
    if (!selectedKolId || !jobId) {
      setError('Select a KOL and ensure job context is available');
      return;
    }
    setOutreachLoading(true);
    setError(null);
    try {
      const res = await api.post('/ai/discovery/outreach-draft', {
        jobId,
        kolProfileId: selectedKolId,
      });
      setOutreach(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to draft outreach';
      setError(msg);
    } finally {
      setOutreachLoading(false);
    }
  };

  const isDemo = jobReport?.demo || agencyReport?.demo || vetReport?.demo || outreach?.demo;
  const isCached = jobReport?.cached || agencyReport?.cached || vetReport?.cached || outreach?.cached;

  return (
    <Card className={`border-violet-200/60 bg-violet-50/30 dark:bg-violet-950/20 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserSearch className="h-5 w-5 text-violet-600" />
          {title}
        </CardTitle>
        <CardDescription>
          Identify talent gaps, vet creator profiles, and draft recruitment outreach. Works in demo mode
          without DeepSeek.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runScan} disabled={scanLoading} size="sm">
            {scanLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Run Discovery Scan
              </>
            )}
          </Button>
          {isDemo && <Badge variant="outline">Demo mode</Badge>}
          {isCached && <Badge variant="outline">Cached</Badge>}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {jobReport && (
          <div className="space-y-4 rounded-lg border bg-white/60 dark:bg-black/20 p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">{jobReport.summary}</p>

            <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border">
              <Users className="h-5 w-5 text-violet-600" />
              <div className="text-sm">
                <p className="font-medium">Pool analysis</p>
                <p className="text-gray-600">
                  {jobReport.poolAnalysis.matchingExisting} matches in pool of{' '}
                  {jobReport.poolAnalysis.totalKolsInAgency} — gap: {jobReport.poolAnalysis.gapCount}
                </p>
                <p className="text-xs text-gray-500">{jobReport.poolAnalysis.summary}</p>
              </div>
            </div>

            {jobReport.talentGaps.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Talent gaps</p>
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300">
                  {jobReport.talentGaps.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>
              </div>
            )}

            {jobReport.discoveryCandidates.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Discovery candidates</p>
                <div className="space-y-2">
                  {jobReport.discoveryCandidates.map((c, i) => (
                    <div key={i} className="rounded-lg border p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-medium">{c.name}</span>
                        <Badge variant="outline">{c.platform}</Badge>
                        <span className="text-gray-500">{c.handle}</span>
                        <Badge variant={priorityVariant(c.vettingPriority)}>
                          {c.vettingPriority} priority
                        </Badge>
                        <span className="text-violet-700 font-medium">{c.matchScore}% match</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {c.tier} · ~{c.estimatedFollowers.toLocaleString()} followers
                      </p>
                      <ul className="list-disc list-inside text-gray-600 mt-1">
                        {c.reasons.map((r, j) => (
                          <li key={j}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {jobReport.recruitmentPriorities.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <TrendingUp className="h-4 w-4" /> Recruitment priorities
                </p>
                <ol className="space-y-2">
                  {jobReport.recruitmentPriorities
                    .sort((a, b) => a.priority - b.priority)
                    .map((p, i) => (
                      <li key={i} className="rounded-lg border p-3 text-sm">
                        <p className="font-medium">
                          #{p.priority} {p.action}
                        </p>
                        <p className="text-gray-600">
                          {p.targetNiche} — {p.rationale}
                        </p>
                      </li>
                    ))}
                </ol>
              </div>
            )}

            {jobReport.recommendations.length > 0 && (
              <ul className="list-disc list-inside text-sm text-violet-800 dark:text-violet-200">
                {jobReport.recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {agencyReport && (
          <div className="space-y-4 rounded-lg border bg-white/60 dark:bg-black/20 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-3xl font-bold text-violet-600">{agencyReport.portfolioHealth.score}</div>
              <div>
                <p className="font-medium">Portfolio health</p>
                <p className="text-sm text-gray-600">{agencyReport.summary}</p>
                <p className="text-xs text-gray-500">{agencyReport.portfolioHealth.summary}</p>
              </div>
            </div>

            {agencyReport.trendingNiches.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Trending niches</p>
                <div className="space-y-2">
                  {agencyReport.trendingNiches.map((n, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 text-sm rounded-lg border p-3">
                      <span className="font-medium">{n.niche}</span>
                      <Badge variant={momentumVariant(n.momentum)}>{n.momentum}</Badge>
                      <span className="text-gray-600">{n.suggestedAction}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {agencyReport.underrepresentedCategories.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Underrepresented categories</p>
                <div className="flex flex-wrap gap-2">
                  {agencyReport.underrepresentedCategories.map((c, i) => (
                    <Badge key={i} variant="outline">
                      {c}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {agencyReport.recruitmentPipeline.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Recruitment pipeline</p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {agencyReport.recruitmentPipeline.map((p, i) => (
                    <div key={i} className="rounded-lg border p-3 text-sm">
                      <p className="font-medium">{p.stage}</p>
                      <p className="text-violet-700">{p.count} profiles</p>
                      <p className="text-xs text-gray-500">{p.bottleneck}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {agencyReport.recommendations.length > 0 && (
              <ul className="list-disc list-inside text-sm text-violet-800 dark:text-violet-200">
                {agencyReport.recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {kols.length > 0 && (
          <div className="border-t pt-6 space-y-4">
            <p className="text-sm font-medium">Vet & recruit from your roster</p>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                value={selectedKolId}
                onChange={(e) => setSelectedKolId(e.target.value)}
              >
                {kols.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
              <Button onClick={runVet} disabled={vetLoading} size="sm" variant="outline">
                {vetLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Vetting...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Vet Profile
                  </>
                )}
              </Button>
              {mode === 'job' && jobId && (
                <Button onClick={runOutreach} disabled={outreachLoading} size="sm" variant="outline">
                  {outreachLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Drafting...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Draft Outreach
                    </>
                  )}
                </Button>
              )}
            </div>

            {vetReport && (
              <div className="rounded-lg border p-4 text-sm space-y-3">
                <p className="font-medium flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  {vetReport.kolName} — overall {vetReport.overallScore}/100
                </p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded border p-2">
                    <p className="text-xs text-gray-500">Authenticity</p>
                    <p className="font-semibold">{vetReport.authenticityScore}</p>
                  </div>
                  <div className="rounded border p-2">
                    <p className="text-xs text-gray-500">Brand safety</p>
                    <p className="font-semibold">{vetReport.brandSafetyScore}</p>
                  </div>
                  <div className="rounded border p-2">
                    <p className="text-xs text-gray-500">Content quality</p>
                    <p className="font-semibold">{vetReport.contentQualityScore}</p>
                  </div>
                </div>
                {vetReport.flags.length > 0 && (
                  <div>
                    {vetReport.flags.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm mb-1">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <Badge variant={severityVariant(f.severity)}>{f.severity}</Badge>
                        <span>{f.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
                {vetReport.strengths.length > 0 && (
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                    {vetReport.strengths.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {outreach && (
              <div className="rounded-lg border p-4 text-sm space-y-2 bg-violet-50/50 dark:bg-violet-950/30">
                <p className="font-medium">{outreach.subject}</p>
                <p className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">{outreach.message}</p>
                <p className="text-xs text-gray-500">Tone: {outreach.tone}</p>
                {outreach.personalizationTips.length > 0 && (
                  <ul className="list-disc list-inside text-xs text-gray-600">
                    {outreach.personalizationTips.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
